import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from "react";
import { StepLabel, StepConnector, Button, Typography, Paper } from "@material-ui/core";
import Stepper from "@material-ui/core/Stepper";
import MobileStepper from "@material-ui/core/MobileStepper";
import Step from "@material-ui/core/Step";
import { getContrastRatio, alpha, makeStyles } from "@material-ui/core/styles";
import StepButton from "@material-ui/core/StepButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import clsx from "clsx";
import { useIntl } from "react-intl";
import { usePageState } from "../../../PageContext";
import { usePages } from "../../../pagesContext";
import { StepperAnnouncement, stepperMessages } from "../../steppers/StepperAnnouncement";
import { touchAllVisibleFields } from "../../../components/TouchVisibleFieldsOnPreviousPages";
import { useFormikContext } from "formik";
import { useResizeCallback } from "../../useResizeCallback";
import { useTemplateSectionIsVisible, useTemplateFieldIsVisible, useFormHasErrors } from "../../../hooks";
import { getStepperHelpers } from "../DirWeb";
import { defineMessages } from "react-intl";
const messages = defineMessages({
    previousStep: {
        id: "template-form.wizard-stepper.previous-step",
        defaultMessage: "Previous step"
    },
    nextStep: {
        id: "template-form.wizard-stepper.next-step",
        defaultMessage: "Next step"
    }
});
const useStyles = makeStyles(theme => ({
    stepper: {
        padding: "12px"
    },
    step: {
        flex: "1 1 auto"
    },
    stepActiveOrCompleted: {},
    stepConnector: {
        fontSize: "1.2rem"
    },
    stepButton: {
        margin: "-12px",
        padding: "12px",
        borderBottom: "4px solid " + getWizardStepperColor(theme).disabledColor,
        "&$stepButtonActiveOrCompleted": {
            borderBottomColor: getWizardStepperColor(theme).activeOrCompleteColor
        }
    },
    stepButtonActiveOrCompleted: {},
    stepIconContainer: {
        paddingRight: 0
    },
    stepIcon: {
        color: theme.palette.common.white,
        "&$stepIconActive": {
            color: theme.palette.common.white,
            fontWeight: 700
        }
    },
    stepIconActive: {},
    stepLabel: {
        fontSize: 12,
        textTransform: "uppercase",
        "&$stepLabelActive": {
            fontWeight: 700
        }
    },
    stepLabelActive: {},
    stepIconText: {
        fill: theme.palette.common.black
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        height: 45,
        backgroundColor: theme.palette.background.paper
    },
    stepLabelMobile: {
        fontSize: 12,
        textTransform: "uppercase",
        fontWeight: 700
    },
    rootMobile: {
        padding: 0
    },
    positionTopMobile: {
        position: "relative"
    },
    progressMobile: {
        width: "100%"
    },
    mobileStepper: {
        [theme.breakpoints.up("sm")]: {
            display: "none"
        }
    },
    mobileStepperOverflow: {
        [theme.breakpoints.up("sm")]: {
            display: "block"
        }
    },
    desktopStepper: {
        display: "none",
        [theme.breakpoints.up("sm")]: {
            display: "flex"
        }
    },
    desktopStepperOverflow: {
        visibility: "hidden",
        height: 0,
        paddingTop: 0,
        paddingBottom: 0
    }
}), { name: "DployWizardStepperStyle" });
const ChevronStepConnector = ({ className }) => (_jsx(ChevronRightIcon, { className: className }));
function getWizardStepperColor(theme) {
    const contrastThreshold = 2.5;
    const main = theme.palette.primary.main;
    const secondary = theme.palette.secondary.main;
    if (getContrastRatio(main, theme.palette.common.white) > contrastThreshold)
        return {
            activeOrCompleteColor: main,
            disabledColor: alpha(main, 0.1)
        };
    if (getContrastRatio(secondary, theme.palette.common.white) > contrastThreshold)
        return {
            activeOrCompleteColor: secondary,
            disabledColor: alpha(secondary, 0.1)
        };
    return {
        activeOrCompleteColor: "rgba(109,162,18,1)",
        disabledColor: "rgba(109,162,18,0.1)"
    };
}
export function WizardStepper(props) {
    const { chevronConnector = true, className, elevation } = props;
    const intl = useIntl();
    const formik = useFormikContext();
    //overflow is true if desktopStepper overflows wrapper
    const [overflow, setOverflow] = useState(false);
    const stepperForDesktopRef = useRef(null);
    const wrapperDivRef = useRef(null);
    useResizeCallback((desktopStepper, wrapper) => {
        setOverflow(desktopStepper.scrollWidth > 0 &&
            wrapper.clientWidth < desktopStepper.scrollWidth);
    }, stepperForDesktopRef, wrapperDivRef);
    const classes = useStyles({ ...props, overflow });
    const { step, goto, labels, getStepHref } = usePageState();
    const pages = usePages();
    const steps = pages.length;
    let stepLabel = 0;
    const { getDisplayNum } = getStepperHelpers(pages);
    const enableFilterLogic = useMemo(() => {
        return pages?.filter(p => p.displayInStepper)?.length > 0;
    }, [pages]);
    const formHasErrors = useFormHasErrors();
    const fieldIsVisible = useTemplateFieldIsVisible();
    const sectionIsVisible = useTemplateSectionIsVisible();
    const { touched, setTouched } = formik;
    const gotoValidPage = (requestedPageIdx) => {
        if (requestedPageIdx <= step)
            return goto(requestedPageIdx);
        const relevantTouched = touchAllVisibleFields(touched, pages.slice(0, requestedPageIdx), fieldIsVisible, sectionIsVisible);
        for (let pageIdx = requestedPageIdx; pageIdx > step; pageIdx--) {
            if (!formHasErrors({ ...formik, touched: relevantTouched }, pages.slice(0, pageIdx), false)) {
                setTouched(touchAllVisibleFields(touched, pages.slice(0, Math.min(pageIdx + 1, requestedPageIdx)), fieldIsVisible, sectionIsVisible));
                return goto(pageIdx);
            }
        }
        setTouched(touchAllVisibleFields(touched, pages.slice(0, step + 1), fieldIsVisible, sectionIsVisible));
    };
    const stepperForMobile = (_jsxs("div", { className: clsx(classes.mobileStepper, {
            [classes.mobileStepperOverflow]: overflow
        }), children: [_jsxs(Paper, { elevation: 0, className: classes.header, children: [_jsx(Button, { component: "a", href: getStepHref(step - 1), size: "small", onClick: e => {
                            e.preventDefault();
                            gotoValidPage(step - 1);
                        }, disabled: step === 0, "aria-label": intl.formatMessage(messages.previousStep), children: _jsx(KeyboardArrowLeft, {}) }), _jsx(Typography, { className: classes.stepLabelMobile, children: step + 1 + ". " + labels[step] }), _jsx(Button, { component: "a", size: "small", href: getStepHref(step + 1), onClick: e => {
                            e.preventDefault();
                            gotoValidPage(step + 1);
                        }, disabled: step === steps - 1, "aria-label": intl.formatMessage(messages.nextStep), children: _jsx(KeyboardArrowRight, {}) })] }), _jsx(MobileStepper, { classes: {
                    root: classes.rootMobile,
                    positionTop: classes.positionTopMobile,
                    progress: classes.progressMobile
                }, variant: "progress", position: "top", steps: steps, activeStep: step, backButton: _jsx(_Fragment, {}), nextButton: _jsx(_Fragment, {}) })] }));
    const stepperForDesktop = (_jsx(Stepper, { className: clsx(classes.stepper, classes.desktopStepper, {
            [classes.desktopStepperOverflow]: overflow
        }), activeStep: enableFilterLogic ? getDisplayNum(step) : step, connector: chevronConnector ? (_jsx(ChevronStepConnector, { className: classes.stepConnector })) : (_jsx(StepConnector, { className: classes.stepConnector })), ref: stepperForDesktopRef, children: pages.map((page, i) => {
            stepLabel =
                page.displayInStepper || !enableFilterLogic
                    ? stepLabel + 1
                    : stepLabel;
            return page.displayInStepper || !enableFilterLogic ? (_jsx(Step, { className: classes.step, children: _jsx(StepButton, { className: clsx({
                        [classes.stepButtonActiveOrCompleted]: i <= step
                    }, classes.stepButton), component: "a", href: getStepHref(i), onClick: e => {
                        e.preventDefault();
                        gotoValidPage(i);
                    }, disabled: i === step ||
                        (i > step && formHasErrors(formik, pages.slice(0, i), false)), "aria-current": i === step ? "step" : undefined, children: _jsx(StepLabel, { classes: {
                            label: classes.stepLabel,
                            active: classes.stepLabelActive,
                            iconContainer: classes.stepIconContainer
                        }, StepIconProps: {
                            completed: false,
                            icon: `${stepLabel}.`,
                            classes: {
                                root: classes.stepIcon,
                                active: classes.stepIconActive,
                                text: classes.stepIconText
                            }
                        }, children: page.pageTitle }) }) }, page.pageTitle || i)) : null;
        }) }));
    return (_jsxs("nav", { "aria-label": intl.formatMessage(stepperMessages.formStepsNav), children: [_jsx(StepperAnnouncement, {}), _jsxs(Paper, { ref: wrapperDivRef, className: className, square: true, elevation: elevation, children: [stepperForMobile, stepperForDesktop] })] }));
}
WizardStepper.displayName = "DployWizardStepper";
//# sourceMappingURL=WizardStepper.js.map